<script setup lang="ts">
import { computeTrialEndSeconds, splitTax, useBillingUtils } from '@/use/billingUtils';
import { useI18n } from '@/i18n';
import { simplifyLineDescription } from '@/lib/invoiceFormat';
import {
  couponInCurrency,
  couponLabel,
  couponMonths,
  discountCoupons,
  discountDisplayName,
  type DiscountRow,
} from '@/lib/stripeDiscounts';
import { useBillingStore } from '@/stores/billing';
import type { Stripe } from 'stripe';
import { computed } from 'vue';
import AppSkeleton from '@/components/AppSkeleton/AppSkeleton.vue';

const props = defineProps<{
  upcomingInvoice: Stripe.Invoice | null;
  recurringInvoice: Stripe.Invoice | null;
  isLoadingInvoice: boolean;
  planName: string;
  planDetail?: string;
  planAmount: number;
  planCurrency: string;
  planInterval: string;
}>();

const { t } = useI18n();
const { formatPrice, formatUnixDate, couponAmount, couponNameAndTerms } = useBillingUtils();
const billingStore = useBillingStore();

const intervalLabel = computed(() => t(`checkout_interval_${props.planInterval}_subscription`));

const invoiceDiscount = computed<Stripe.Discount | null>(() => {
  const inv = props.upcomingInvoice;
  if (!inv) return null;
  const d = inv.discounts?.[0];
  if (!d || typeof d === 'string') return null;
  return d as Stripe.Discount;
});

const discountCoupon = computed<Stripe.Coupon | null>(() => {
  const d = invoiceDiscount.value as Record<string, unknown> | null;
  if (!d) return null;
  const direct = d.coupon;
  if (direct && typeof direct === 'object') return direct as Stripe.Coupon;
  const src = d.source as Record<string, unknown> | undefined;
  const nested = src?.coupon;
  if (nested && typeof nested === 'object') return nested as Stripe.Coupon;
  return null;
});

const discountLabel = computed(() => {
  const coupon = discountCoupon.value;
  return coupon ? couponNameAndTerms(discountDisplayName(invoiceDiscount.value as DiscountRow | null), coupon) : '';
});

// The coupons a coupon line can stand for: the one waiting for checkout, or one running on the
// subscription that a switch to yearly billing turns into a line.
const knownCoupons = computed(() => {
  const pending = billingStore.effectiveCheckoutCoupon?.coupon;
  const running = discountCoupons((billingStore.primarySubscription ?? {}) as DiscountRow);
  return pending ? [pending, ...running] : running;
});

// dabble-rest marks the unused-time credit in metadata; a server from before that names it only
// in English (the lifetime purchase's credit), so the description stays a fallback until it ships.
const isUnusedTimeCredit = (line: Stripe.InvoiceLineItem) =>
  line.metadata?.credit === 'unused_time' || line.description === 'Credit for unused time on your current plan';

/**
 * A coupon worth its months as a line on this invoice, labelled with its terms over the months the
 * line carries: a once coupon is one month's worth of a yearly bill, and a running coupon carried
 * onto yearly billing has only what it still owes.
 */
function couponLineLabel(line: Stripe.InvoiceLineItem, id: string): string {
  const description = simplifyLineDescription(line.description);
  const known = knownCoupons.value.find((c) => c.id === id);
  if (!known) return description;
  const coupon = couponInCurrency(known, props.upcomingInvoice?.currency);
  const months = Number(line.metadata?.coupon_months) || couponMonths(coupon);
  return couponNameAndTerms(couponLabel(coupon) || description, {
    ...coupon,
    duration: 'repeating',
    duration_in_months: months,
  });
}

const invoiceCustomer = computed<Stripe.Customer | null>(() => {
  const raw = props.upcomingInvoice?.customer;
  if (!raw || typeof raw === 'string') return null;
  return raw as Stripe.Customer;
});

const appliedBalance = computed(() => {
  const inv = props.upcomingInvoice;
  const cust = invoiceCustomer.value;
  if (!inv || !cust?.balance || !inv.total) return null;
  return Math.max(-inv.total, cust.balance);
});

// Exclusive tax (US/CA) reconciles as its own line; inclusive tax (EU VAT) is
// already in the price, so it shows as a note below the total rather than a row.
const upcomingTax = computed(() => splitTax(props.upcomingInvoice?.total_taxes));

// Trial end date drives the amount-due label ("due today" vs "due on {date}"). Shared
// with CheckoutView via computeTrialEndSeconds so both agree on what counts as a trial.
const trialEndSeconds = computed<number | null>(() =>
  computeTrialEndSeconds({
    subscription: billingStore.primarySubscription,
    upcomingInvoice: props.upcomingInvoice,
    recurringInvoice: props.recurringInvoice,
    planAmount: props.planAmount,
  }),
);

const trialEndDate = computed(() => formatUnixDate(trialEndSeconds.value));

const amountDueLabel = computed(() => {
  const inv = props.upcomingInvoice;
  if (!inv) return '';
  if (inv.amount_due < 0) return t('checkout_applied_to_your_account');
  // Checkout ends a live trial and bills today, but the preview still carries the
  // trial's renewal date — quoting it would promise a charge that already happened.
  const billedToday = billingStore.primarySubscription?.status === 'trialing';
  if (!billedToday && inv.next_payment_attempt && billingStore.primarySubscription && !trialEndDate.value) {
    return t('checkout_amount_due_on_date', {
      date: formatUnixDate(inv.next_payment_attempt),
    });
  }
  return t('checkout_amount_due_today');
});

// Stripe moved the proration flag under `parent.*_details` in recent API
// versions; undefined (no parent detail) is treated as "keep".
function lineIsProration(line: Stripe.InvoiceLineItem): boolean | undefined {
  const details = line.parent?.subscription_item_details ?? line.parent?.invoice_item_details;
  return details?.proration;
}

// Only the adjustments matter in the breakdown: the plain plan charge is already the
// header + renewal line, and $0 lines are pure noise. Credits are always an
// adjustment; the unused-time credit on a lifetime purchase is a plain invoice item,
// not a proration, and dropping it would move the subtotal with nothing to explain it.
const detailLines = computed(() => {
  const lines = props.upcomingInvoice?.lines?.data ?? [];
  return lines
    .filter((l) => l.amount !== 0 && (l.amount < 0 || lineIsProration(l) !== false))
    .map((line, idx) => {
      const coupon = line.metadata?.coupon;
      const label = coupon
        ? couponLineLabel(line, coupon)
        : isUnusedTimeCredit(line)
          ? t('checkout_unused_time_credit')
          : simplifyLineDescription(line.description);
      return { key: line.id ?? idx, label, amount: line.amount, discount: Boolean(coupon) };
    });
});

// Date the first real (recurring) charge will hit — first day of the recurring
// invoice's period, which is also when the trial ends.
const recurringStartDate = computed(() => {
  const start = props.recurringInvoice?.lines?.data?.[0]?.period?.start;
  return start ? formatUnixDate(start) : '';
});
</script>

<template>
  <div class="invoice-summary">
    <h2 class="invoice-summary__title">{{ t('checkout_order_summary') }}</h2>

    <div class="invoice-summary__row invoice-summary__row--plan">
      <div class="invoice-summary__plan-info">
        <span class="invoice-summary__plan-name">{{ planName }}</span>
        <span class="invoice-summary__plan-interval">{{ planDetail ?? intervalLabel }}</span>
      </div>
      <span class="invoice-summary__row-value">{{ formatPrice(planAmount, planCurrency) }}</span>
    </div>

    <div v-if="isLoadingInvoice && !upcomingInvoice" class="invoice-summary__skeleton">
      <AppSkeleton height="0.8rem" width="55%" />
      <AppSkeleton height="0.8rem" />
      <AppSkeleton height="1.1rem" width="45%" />
    </div>

    <template v-else-if="upcomingInvoice">
      <div class="invoice-summary__body">
        <div
          v-for="line in detailLines"
          :key="line.key"
          class="invoice-summary__row"
          :class="{ 'invoice-summary__row--discount': line.discount }"
        >
          <span class="invoice-summary__row-label">{{ line.label }}</span>
          <span class="invoice-summary__row-value">{{ formatPrice(line.amount, upcomingInvoice.currency) }}</span>
        </div>

        <div class="invoice-summary__row">
          <span class="invoice-summary__row-label">{{ t('checkout_subtotal') }}</span>
          <span class="invoice-summary__row-value">{{
            formatPrice(upcomingInvoice.subtotal, upcomingInvoice.currency)
          }}</span>
        </div>

        <div v-if="discountCoupon" class="invoice-summary__row invoice-summary__row--discount">
          <span class="invoice-summary__row-label">{{ discountLabel }}</span>
          <span class="invoice-summary__row-value">({{ couponAmount(discountCoupon) }})</span>
        </div>

        <div v-if="upcomingTax.exclusive !== 0" class="invoice-summary__row">
          <span class="invoice-summary__row-label">{{ t('checkout_tax') }}</span>
          <span class="invoice-summary__row-value">{{
            formatPrice(upcomingTax.exclusive, upcomingInvoice.currency)
          }}</span>
        </div>

        <div v-if="appliedBalance" class="invoice-summary__row">
          <span class="invoice-summary__row-label">{{ t('checkout_applied_balance') }}</span>
          <span class="invoice-summary__row-value">{{ formatPrice(appliedBalance, upcomingInvoice.currency) }}</span>
        </div>
      </div>

      <div class="invoice-summary__row invoice-summary__row--total">
        <span class="invoice-summary__row-label">{{ amountDueLabel }}</span>
        <span class="invoice-summary__row-value">{{
          formatPrice(upcomingInvoice.amount_due, upcomingInvoice.currency)
        }}</span>
      </div>

      <div v-if="upcomingTax.inclusive !== 0" class="invoice-summary__tax-note">
        {{ t('checkout_tax_included_note', { amount: formatPrice(upcomingTax.inclusive, upcomingInvoice.currency) }) }}
      </div>
    </template>

    <div v-if="recurringInvoice" class="invoice-summary__renewal">
      <span class="invoice-summary__renewal-label">{{
        recurringStartDate ? t('checkout_renews_on', { date: recurringStartDate }) : t('checkout_renews')
      }}</span>
      <span class="invoice-summary__renewal-value">{{
        formatPrice(recurringInvoice.total, recurringInvoice.currency)
      }}</span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.invoice-summary {
  display: flex;
  flex-direction: column;
  gap: space('small');
  min-width: 0;

  &__title {
    margin: 0;
    font-size: fontSize('xsmall');
    font-weight: fontWeight('semibold');
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: theme('color-text-placeholder');
  }

  &__skeleton {
    display: flex;
    flex-direction: column;
    gap: space('small');
    padding: space('small') 0;
  }

  &__plan-info {
    display: flex;
    flex-direction: column;
    gap: space('xtiny');
    min-width: 0;
  }

  &__plan-name {
    font-size: fontSize('default');
    font-weight: fontWeight('semibold');
    color: theme('color-text-primary');
  }

  &__plan-interval {
    font-size: fontSize('small');
    color: theme('color-text-secondary');
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: space('xsmall');
  }

  &__row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: space('small');
    font-size: fontSize('small');

    &--plan {
      align-items: flex-start;
      padding-bottom: space('small');
      border-bottom: 1px solid theme('color-border-primary');
      font-size: fontSize('default');
    }
  }

  &__row-label {
    flex: 1;
    min-width: 0;
    color: theme('color-text-secondary');
  }

  &__row-value {
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
    color: theme('color-text-secondary');
  }

  &__row--plan &__row-value {
    color: theme('color-text-primary');
    font-weight: fontWeight('semibold');
  }

  &__row--discount &__row-value {
    color: theme('color-fg-success');
  }

  &__row--total {
    padding: space('xsmall') space('small');
    // Bleed the highlight to the card's inner padding while keeping the text
    // aligned with the rows above; replaces the divider that used to sit here.
    margin-inline: calc(-1 * #{space('small')});
    background: theme('color-bg-neutral-subtle');
    border-radius: borderRadius('small');
    font-size: 1rem;
    font-weight: fontWeight('bold');

    .invoice-summary__row-label,
    .invoice-summary__row-value {
      color: theme('color-text-primary');
    }
  }

  &__tax-note {
    font-size: fontSize('xsmall');
    color: theme('color-text-secondary');
    text-align: right;
  }

  &__renewal {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: space('small');
    font-size: fontSize('xsmall');
    color: theme('color-text-secondary');
  }

  &__renewal-value {
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
}
</style>
